import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";

import MyBidsSection from "../components/MyBidsSection";
import IdentityVerificationCard from "../components/IdentityVerificationCard";
import NotificationBell from "../components/NotificationBell";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  LayoutDashboard, MessageSquare, Users, MapPin, UserCheck, CheckSquare,
  FileText, Calendar, Clock, Timer, BarChart3, Settings, ChevronLeft, ChevronRight,
  Briefcase, DollarSign, Eye, CheckCircle2, AlertCircle, Loader2, Crown, 
  CreditCard, Search, Filter, Shield, FileCheck, ExternalLink, Bell, Inbox, 
  ArrowRight, Smartphone, Zap, Fingerprint, Phone, PhoneIncoming, PhoneOutgoing,
  MoreVertical, ArrowUpDown, X, Menu, LogOut, User, Info
} from "lucide-react";

// Sidebar Navigation Items
const SIDEBAR_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/pro/dashboard" },
  { id: "leads", label: "Leads", icon: Users, path: "/pro/leads" },
  { id: "conversations", label: "Conversations", icon: MessageSquare, path: "/pro/conversations" },
  { id: "leads-map", label: "Leads Map", icon: MapPin, path: "/pro/leads-map" },
  { id: "clients", label: "Clients", icon: UserCheck, path: "/pro/clients" },
  { id: "tasks", label: "Tasks", icon: CheckSquare, path: "/pro/tasks" },
  { id: "estimates", label: "Estimates", icon: FileText, path: "/pro/estimates" },
  { id: "appointments", label: "Appointments", icon: Calendar, path: "/pro/appointments" },
  { id: "schedule", label: "Agent Schedule", icon: Clock, path: "/pro/schedule" },
  { id: "time-clock", label: "Time Clock", icon: Timer, path: "/pro/timeclock" },
  { id: "reports", label: "Reports", icon: BarChart3, path: "/pro/reports" },
  { id: "billing", label: "Billing", icon: CreditCard, path: "/pro/billing" },
  { id: "notifications", label: "Notification Settings", icon: Bell, path: "/pro/notification-settings" },
  { id: "account", label: "Account", icon: Settings, path: "/settings" },
];

// Sidebar Component
const ProSidebar = ({ collapsed, setCollapsed, activeItem, user, onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-[#1e293b] text-white z-40 transition-all duration-300 ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="pro-sidebar"
    >
      {/* Logo Area */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/ec88479e-de0b-4b07-83a2-d0fc0e0593e3/attachments/a0930ce5021847efb76339185d8cd461_image.bin" 
              alt="BQL Leads" 
              className="h-8 w-auto brightness-0 invert"
              onError={(e) => { e.target.src = "/bql-logo.png"; }}
            />
          </Link>
        )}
        <div className="flex items-center gap-1">
          {!collapsed && <NotificationBell />}
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <User className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.full_name}</p>
              <p className="text-xs text-white/60 truncate">{user?.business_name}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gold/20 text-gold text-xs rounded-full">
              <Crown className="w-3 h-3" />
              Pro Member
            </span>
            {user?.identity_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                <Fingerprint className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="p-2 flex-1 overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = activeItem === item.id;
          
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-lg mb-1 transition-all cursor-pointer active:scale-[0.98] ${
                isActive 
                  ? "bg-gold/20 text-gold border-l-2 border-gold" 
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              } ${collapsed ? "justify-center" : ""}`}
              data-testid={`sidebar-${item.id}`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-gold" : ""}`} />
              {!collapsed && (
                <>
                  <span className="flex-1 text-sm font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded text-white/50">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-white/10">
        <button
          onClick={onLogout}
          className={`flex items-center gap-3 px-3 py-3 min-h-[44px] rounded-lg w-full text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer active:scale-[0.98] ${
            collapsed ? "justify-center" : ""
          }`}
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="text-sm font-medium">Log Out</span>}
        </button>
      </div>
    </aside>
  );
};

// Mobile Header
const MobileHeader = ({ user, onMenuClick, onLogout }) => (
  <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#1e293b] text-white z-30 flex items-center justify-between px-4">
    <button 
      onClick={onMenuClick} 
      className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-white/10 rounded-lg active:scale-95 transition-transform"
      data-testid="mobile-menu-btn"
    >
      <Menu className="w-6 h-6" />
    </button>
    <Link to="/">
      <img 
        src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/ec88479e-de0b-4b07-83a2-d0fc0e0593e3/attachments/a0930ce5021847efb76339185d8cd461_image.bin" 
        alt="BQL Leads" 
        className="h-8 w-auto brightness-0 invert"
        onError={(e) => { e.target.src = "/bql-logo.png"; }}
      />
    </Link>
    <div className="flex items-center gap-1">
      <NotificationBell />
      <button 
        onClick={onLogout} 
        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-white/10 rounded-lg text-red-400 active:scale-95 transition-transform"
        data-testid="mobile-logout-btn"
      >
        <LogOut className="w-5 h-5" />
      </button>
    </div>
  </header>
);

// Metrics Card Component
const MetricCard = ({ icon: Icon, label, value, subValue, trend, color = "gold" }) => (
  <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium mb-1">{label}</p>
          <p className={`text-3xl font-bold ${color === "gold" ? "text-[#d4a853]" : "text-[#1e3a5f]"}`}>
            {value}
          </p>
          {subValue && <p className="text-xs text-slate-400 mt-1">{subValue}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          color === "gold" ? "bg-[#d4a853]/10" : "bg-[#1e3a5f]/10"
        }`}>
          <Icon className={`w-6 h-6 ${color === "gold" ? "text-[#d4a853]" : "text-[#1e3a5f]"}`} />
        </div>
      </div>
      {trend && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <span className={`text-xs font-medium ${trend > 0 ? "text-emerald-600" : "text-red-500"}`}>
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}% from last month
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Lead Status Badge
const LeadStatusBadge = ({ status }) => {
  const styles = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    contacted: "bg-yellow-100 text-yellow-700 border-yellow-200",
    in_progress: "bg-purple-100 text-purple-700 border-purple-200",
    won: "bg-emerald-100 text-emerald-700 border-emerald-200",
    lost: "bg-red-100 text-red-700 border-red-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  
  const labels = {
    new: "New",
    contacted: "Contacted",
    in_progress: "In Progress",
    won: "Won",
    lost: "Lost",
    completed: "Completed",
  };
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.new}`}>
      {labels[status] || status}
    </span>
  );
};

// Leads Table Component
const LeadsTable = ({ leads, loading, onViewLead }) => {
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const filteredLeads = leads.filter(lead => {
    if (filter !== "all" && lead.status !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        lead.title?.toLowerCase().includes(query) ||
        lead.customer_name?.toLowerCase().includes(query) ||
        lead.city?.toLowerCase().includes(query) ||
        lead.service_category?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (sortBy === "created_at") {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    return sortOrder === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardContent className="p-0">
        {/* Table Header */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h3 className="text-lg font-bold text-[#1e3a5f]">Leads</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64 h-9 text-sm"
                  data-testid="leads-search"
                />
              </div>
            </div>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {[
              { id: "all", label: "All" },
              { id: "new", label: "New" },
              { id: "in_progress", label: "In Progress" },
              { id: "completed", label: "Completed" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === tab.id
                    ? "bg-[#1e3a5f] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <button onClick={() => toggleSort("direction")} className="flex items-center gap-1 hover:text-slate-700">
                    Direction
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <button onClick={() => toggleSort("customer_name")} className="flex items-center gap-1 hover:text-slate-700">
                    From
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Group</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <button onClick={() => toggleSort("status")} className="flex items-center gap-1 hover:text-slate-700">
                    Status
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Lead ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
                    <p className="text-slate-500 mt-2">Loading leads...</p>
                  </td>
                </tr>
              ) : sortedLeads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">No leads found</p>
                    <p className="text-slate-400 text-sm mt-1">
                      {filter !== "all" ? "Try changing your filter" : "New leads will appear here"}
                    </p>
                  </td>
                </tr>
              ) : (
                sortedLeads.map((lead, index) => (
                  <tr 
                    key={lead.id || index} 
                    className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                    data-testid={`lead-row-${index}`}
                  >
                    <td className="px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <PhoneIncoming className="w-4 h-4 text-emerald-600" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{lead.customer_name || "Customer"}</p>
                        <p className="text-xs text-slate-500">{lead.title || lead.service_category}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded text-xs font-medium">
                        {lead.service_category || "General"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {lead.city || lead.location || "NJ"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <LeadStatusBadge status={lead.status || "new"} />
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        {(lead.id || "").substring(0, 8)}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewLead(lead)}
                        className="text-[#1e3a5f] border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5"
                        data-testid={`view-lead-${index}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {sortedLeads.length > 0 && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {sortedLeads.length} of {leads.length} leads
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>Previous</Button>
              <Button variant="outline" size="sm" disabled>Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
export default function ProfessionalDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, total_earnings: 0, total_bids: 0, accepted_bids: 0 });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  // Platform is FREE - no subscription check needed. All pros have full access.
  // First lead is FREE, then $29.99/month subscription starts

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads/assigned`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      
      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setLeads(responses[1].data.leads || []);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleViewLead = (lead) => {
    navigate(`/leads/${lead.id || lead.job_id}`);
  };

  // Determine active sidebar item
  const activeItem = location.pathname.includes("/leads") ? "leads" : "dashboard";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <MobileHeader 
        user={user} 
        onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
        onLogout={handleLogout}
      />

      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <ProSidebar
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          activeItem={activeItem}
          user={user}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <ProSidebar
            collapsed={false}
            setCollapsed={() => {}}
            activeItem={activeItem}
            user={user}
            onLogout={handleLogout}
          />
        </div>
      )}

      {/* Main Content */}
      <main 
        className={`pt-20 lg:pt-6 pb-12 px-4 lg:px-8 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        }`}
        data-testid="professional-dashboard"
      >
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-[#1e3a5f]">
              Welcome back, {user?.full_name?.split(" ")[0]}!
            </h1>
            <p className="text-slate-500 mt-1">{user?.business_name}</p>
          </div>

          {/* First Lead Fee Info Banner */}
          {!user?.first_lead_charged && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="first-lead-fee-info">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                <strong>Your first lead is free.</strong> After that, $29.99/month — cancel anytime.
              </p>
            </div>
          )}

          {/* Identity Verification */}
          {!user?.identity_verified && (
            <div className="mb-6">
              <IdentityVerificationCard onVerificationStart={refreshUser} />
            </div>
          )}

          {/* Metrics Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="metrics-bar">
            <MetricCard 
              icon={Users} 
              label="Total Leads" 
              value={leads.length || stats.available_leads || 0}
              subValue="This month"
              color="gold"
            />
            <MetricCard 
              icon={DollarSign} 
              label="Total Earnings" 
              value={`$${stats.total_earnings || 0}`}
              subValue="All time"
              color="navy"
            />
            <MetricCard 
              icon={Briefcase} 
              label="Active Bids" 
              value={stats.total_bids || 0}
              subValue="Pending responses"
              color="gold"
            />
            <MetricCard 
              icon={CheckCircle2} 
              label="Won Jobs" 
              value={stats.accepted_bids || 0}
              subValue="Completed"
              color="navy"
            />
          </div>

          {/* My Bids Section */}
          <div className="mb-8">
            <MyBidsSection />
          </div>

          {/* Leads Table */}
          <LeadsTable 
            leads={leads} 
            loading={loading} 
            onViewLead={handleViewLead}
          />

          {/* Mobile App Banner - Clean minimal style */}
          <Card className="mt-8 bg-white border-0 shadow-sm">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-[#1e3a5f]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[#1e3a5f]">BQL Mobile App — Coming Soon!</h3>
                    <p className="text-slate-500 text-sm">Get instant lead notifications on your phone</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium text-slate-600">
                    iOS Soon
                  </span>
                  <span className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium text-slate-600">
                    Android Soon
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
